import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2, Plug, Download, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Beds24Tab() {
  const [configured, setConfigured] = useState(false);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({}); // accId -> beds24_room_id string
  const [rooms, setRooms] = useState(null);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [syncing, setSyncing] = useState("");

  const load = useCallback(() => {
    api.get("/admin/beds24/config").then((r) => {
      setConfigured(r.data.configured);
      setRows(r.data.accommodations);
      const m = {};
      r.data.accommodations.forEach((a) => { m[a.id] = a.beds24_room_id ?? ""; });
      setMapping(m);
    }).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveMapping = async (accId) => {
    try {
      const { data } = await api.put(`/admin/beds24/mapping/${accId}`, { beds24_room_id: mapping[accId] === "" ? null : mapping[accId] });
      setMapping((m) => ({ ...m, [accId]: data.beds24_room_id ?? "" }));
      toast.success("Association enregistrée");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const loadRooms = async () => {
    setLoadingRooms(true);
    try {
      const { data } = await api.get("/admin/beds24/properties");
      setRooms(data.rooms || []);
      toast.success(`${data.rooms?.length || 0} logement(s) Beds24 trouvé(s)`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoadingRooms(false); }
  };

  const sync = async (accId) => {
    setSyncing(accId || "all");
    try {
      const { data } = await api.post("/admin/beds24/sync", accId ? { accommodation_id: accId } : {});
      const err = (data.results || []).find((r) => r.error);
      if (err) toast.error(`Erreur : ${err.error}`);
      toast.success(`${data.total_imported} tarif(s) · ${data.total_blocked} blocage(s) synchronisé(s)`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSyncing(""); }
  };

  return (
    <div data-testid="beds24-tab">
      <div className="rounded-2xl bg-brand-50/60 border border-brand/15 p-5 mb-6 text-sm text-ink-600 leading-relaxed">
        <p className="font-semibold text-ink-900 mb-1">Beds24, pilote des tarifs et disponibilités</p>
        <p>Beds24 est la <strong>source de référence</strong> : les prix, séjours minimums et disponibilités saisis dans Beds24 sont importés automatiquement sur le site et les dates indisponibles y sont bloquées. Chaque réservation porte son identifiant de chambre exact (plus d'erreur d'attribution).</p>
        <p className="mt-1">Associez chaque logement du site à son <strong>identifiant de chambre Beds24 (room id)</strong>, puis lancez une synchronisation. La synchronisation automatique tourne régulièrement.</p>
        <p className="mt-1"><strong>Réservations du site → Beds24 :</strong> chaque réservation (en ligne dès l'acompte payé, ou manuelle par téléphone) est envoyée automatiquement dans Beds24 pour bloquer les dates sur Airbnb/Booking et éviter les doubles réservations.</p>
      </div>

      {configured ? (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 text-emerald-700 px-4 py-3 mb-5 text-sm" data-testid="beds24-status-ok">
          <CheckCircle2 size={18} /> Connexion Beds24 active. La synchronisation est opérationnelle.
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-xl bg-amber-50 text-amber-800 px-4 py-3 mb-5 text-sm" data-testid="beds24-status-missing">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <span>Beds24 <strong>non encore connecté</strong>. Générez un code d'invitation dans Beds24 (Settings → API) pour activer la synchronisation.</span>
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-2 mb-4">
        <button onClick={loadRooms} disabled={!configured || loadingRooms} className="inline-flex items-center gap-2 rounded-full border border-brand text-brand px-4 py-2 text-sm font-medium hover:bg-brand-50 disabled:opacity-50" data-testid="beds24-load-rooms">
          {loadingRooms ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Charger mes logements Beds24
        </button>
        <button onClick={() => sync(null)} disabled={!configured || syncing === "all"} className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-medium hover:bg-brand-600 disabled:opacity-50" data-testid="beds24-sync-all">
          {syncing === "all" ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Tout synchroniser
        </button>
      </div>

      {rooms && (
        <div className="rounded-2xl bg-white border border-black/5 p-4 mb-5" data-testid="beds24-rooms-list">
          <p className="text-xs font-semibold text-ink-700 mb-2">Logements Beds24 détectés (room id · nom)</p>
          <div className="flex flex-wrap gap-2">
            {rooms.map((a) => (
              <span key={a.room_id} className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 text-brand px-3 py-1 text-xs">
                <Plug size={12} /> {a.room_id} · {a.room_name}
              </span>
            ))}
            {rooms.length === 0 && <span className="text-xs text-ink-400">Aucun logement trouvé.</span>}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {rows.map((a) => (
          <div key={a.id} className="rounded-2xl bg-white border border-black/5 p-5" data-testid={`beds24-acc-${a.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <h3 className="font-heading text-xl text-ink-900">{a.name}</h3>
                <p className="text-xs text-ink-400 capitalize">{a.type} · {a.blocked_count} date(s) bloquée(s){a.last_sync ? ` · sync ${a.last_sync.slice(0, 10)}` : ""}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Room id Beds24</label>
                <input value={mapping[a.id] ?? ""} onChange={(e) => setMapping((m) => ({ ...m, [a.id]: e.target.value }))}
                  placeholder="ex : 714067" className="w-40 rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand" data-testid={`beds24-mapping-${a.id}`} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => saveMapping(a.id)} className="rounded-full border border-ink-400/30 text-ink-700 px-4 py-2 text-sm font-medium hover:bg-black/5" data-testid={`beds24-save-${a.id}`}>Associer</button>
                <button onClick={() => sync(a.id)} disabled={!configured || !mapping[a.id] || syncing === a.id} className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-600 disabled:opacity-50" data-testid={`beds24-sync-${a.id}`}>
                  {syncing === a.id ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Synchroniser
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
